import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, Layers, BookOpen, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const KEY = "chronos.onboarding.v1";

export default function Onboarding() {
  const { t } = useTranslation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (location.pathname !== "/") { setVisible(false); return; }
    try {
      if (!localStorage.getItem(KEY)) setVisible(true);
    } catch (e) { /* ignore */ }
  }, [location.pathname]);

  useEffect(() => {
    const replay = () => { setStep(0); setVisible(true); };
    window.addEventListener("chronos:replay-onboarding", replay);
    return () => window.removeEventListener("chronos:replay-onboarding", replay);
  }, []);

  const finish = () => {
    try { localStorage.setItem(KEY, JSON.stringify({ done: true, at: new Date().toISOString() })); } catch (e) {}
    setVisible(false);
  };

  const steps = [
    { icon: Sparkles, colorBg: "from-amber-500/25 to-orange-500/10", accent: "text-amber-300",
      title: t("onboarding.step_1_title"), text: t("onboarding.step_1_text") },
    { icon: Layers, colorBg: "from-emerald-500/25 to-teal-500/10", accent: "text-emerald-300",
      title: t("onboarding.step_2_title"), text: t("onboarding.step_2_text") },
    { icon: BookOpen, colorBg: "from-fuchsia-500/25 to-rose-500/10", accent: "text-fuchsia-300",
      title: t("onboarding.step_3_title"), text: t("onboarding.step_3_text") },
  ];
  const s = steps[step];
  const Icon = s.icon;
  const last = step === steps.length - 1;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/80 backdrop-blur"
          data-testid="onboarding"
        >
          <motion.div
            initial={{ scale: 0.95, y: 15 }} animate={{ scale: 1, y: 0 }}
            className="glass-strong rounded-3xl p-6 sm:p-8 max-w-md w-full text-center relative overflow-hidden"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${s.colorBg} opacity-70`} aria-hidden />
            <button
              onClick={finish}
              className="absolute top-3 right-3 text-stone-400 hover:text-stone-100 z-10"
              data-testid="onboarding-skip"
              aria-label={t("onboarding.skip")}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative z-10">
              <motion.div
                key={step}
                initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className={`w-20 h-20 mx-auto rounded-full glass flex items-center justify-center ${s.accent} shard-glow mb-4`}
              >
                <Icon className="w-10 h-10" />
              </motion.div>
              <motion.h3
                key={`t-${step}`}
                initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
                className="font-serif text-2xl sm:text-3xl font-black text-stone-50"
              >{s.title}</motion.h3>
              <motion.p
                key={`p-${step}`}
                initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
                className="mt-3 text-sm sm:text-base text-stone-200 leading-relaxed"
              >{s.text}</motion.p>

              <div className="flex justify-center gap-2 mt-6" aria-hidden>
                {steps.map((_, i) => (
                  <span key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === step ? "w-6 bg-amber-300" : "w-1.5 bg-white/30"
                    }`}
                    data-testid={`onboarding-dot-${i}`}
                  />
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  onClick={finish}
                  className="text-sm text-stone-400 hover:text-stone-100"
                  data-testid="onboarding-skip-link"
                >{t("onboarding.skip")}</button>
                <Button
                  onClick={() => last ? finish() : setStep(step + 1)}
                  className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-6"
                  data-testid="onboarding-next"
                >
                  {last ? t("onboarding.start") : t("onboarding.next")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
